import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { sideViewState, useAuth, useConfig } from '@chainlit/react-client';

import ElementSideView from '@/components/ElementSideView';
import LeftSidebar from '@/components/LeftSidebar';
import SessionExpiryBanner from '@/components/SessionExpiryBanner';
import { TaskList } from '@/components/Tasklist';
import { Header } from '@/components/header';
import { ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { SidebarProvider } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useUISettings } from '@/hooks/useUISettings';

import { userEnvState } from 'state/user';

type Props = {
  children: JSX.Element;
};

const Page = ({ children }: Props) => {
  const { config } = useConfig();
  const { data } = useAuth();
  const { settings } = useUISettings();
  const userEnv = useRecoilValue(userEnvState);
  const sideView = useRecoilValue(sideViewState);
  
  // Get sidebar background color from settings
  const sidebarBg = settings?.colors?.sidebarBackground || '#ECF2FF';

  if (config?.userEnv) {
    for (const key of config.userEnv || []) {
      if (!userEnv[key]) return <Navigate to="/env" />;
    }
  }

  const historyEnabled = config?.dataPersistence && data?.requireLogin;
  return (
    <SidebarProvider defaultOpen={false}>
      <div className="h-screen w-screen relative overflow-hidden bg-background"> {/* Added bg-background here for overall page */}        {/* Left Bezel Structure - Hidden on mobile */}
        <div className="fixed left-0 top-0 h-full w-20 bg-black z-20 hidden md:block"> {/* Outer black bar, w-20 (80px) */}
          <div className="absolute right-0 top-0 h-full w-8 rounded-l-3xl z-10" style={{ backgroundColor: sidebarBg }}></div> {/* Inner curve, dynamic color, w-8 (32px) */}
        </div>

        {/* Right Bezel Structure - Hidden on mobile */}
        <div className="fixed right-0 top-0 h-full w-20 bg-black z-20 hidden md:block"> {/* Outer black bar, w-20 (80px) */}
          <div className="absolute left-0 top-0 h-full w-8 bg-background rounded-r-3xl z-10"></div> {/* Inner curve, bg-background, w-8 (32px) */}
        </div>

        {/* Main Application Content Area */}
        <div
          className={cn(
            'relative h-full',
            // Mobile: full width, Desktop: with bezels  
            'mx-0 md:mx-20' // 0 margin on mobile, 80px margins on desktop
          )}
        >
          <div className="flex h-full"> {/* This flex container holds sidebar and main content */}
            {/* Left Sidebar */}
            {historyEnabled && <LeftSidebar />} {/* Sidebar itself has bg-[#ECF2FF] and no left radius */}

            {/* Main Chat Content */}
            <div className="flex flex-col flex-1 h-full bg-background"> {/* This is the white area for chat messages */}
              <Header />
              <SessionExpiryBanner show={historyEnabled} />
              <ResizablePanelGroup
                direction="horizontal"
                className="flex flex-row flex-grow"
              >
                <ResizablePanel
                  className="flex flex-col h-full w-full"
                  minSize={40}
                  defaultSize={60}
                >
                  <div className="flex flex-row flex-grow overflow-auto">
                    {children}
                  </div>
                </ResizablePanel>
                {sideView ? <ElementSideView /> : <TaskList isMobile={false} />}
              </ResizablePanelGroup>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Page;
